/* Yves Swiss — Influencer Gifts (theme-side). Shopify calculates all discount prices. */
(() => {
  'use strict';
  const config = document.getElementById('ys-influencer-gifts');
  if (!config || window.ysInfluencerGifts) return;

  let campaigns;
  try {
    campaigns = JSON.parse(config.textContent || '[]');
  } catch (error) {
    console.error('Influencer gift configuration invalid:', error);
    return;
  }

  const root = window.Shopify?.routes?.root || '/';
  const normalize = value => String(value || '').trim().toLowerCase();
  const isGift = item => !!item.properties?._ys_influencer_gift;

  function isActive(campaign) {
    const now = Date.now();
    return !!campaign.available &&
      Number.isSafeInteger(Number(campaign.variantId)) &&
      (!campaign.startsAt || now >= Date.parse(campaign.startsAt)) &&
      (!campaign.endsAt || now <= Date.parse(campaign.endsAt));
  }

  function match(code) {
    return campaigns.find(c => normalize(c.code) === normalize(code) && isActive(c));
  }

  async function request(path, payload) {
    const response = await fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Cart request failed');
    return data;
  }

  async function getCart() {
    const response = await fetch(root + 'cart.js', { cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to read cart');
    return response.json();
  }

  function applications(data) {
    return [
      ...(data.cart_level_discount_applications || []),
      ...(data.items || []).flatMap(item =>
        (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))
    ];
  }

  function appliedCodes(data) {
    const fromApplications = applications(data)
      .filter(discount => discount?.type === 'discount_code' && discount.title)
      .map(discount => discount.title);
    const fromCart = (data.discount_codes || [])
      .map(discount => typeof discount === 'string' ? discount : (discount?.code || discount?.discount_code))
      .filter(Boolean);
    return [...new Map([...fromApplications, ...fromCart]
      .map(code => [normalize(code), code])).values()];
  }

  function codeApplied(data, code) {
    return appliedCodes(data).some(value => normalize(value) === normalize(code));
  }

  function hasPurchasedItem(data) {
    return (data.items || []).some(item =>
      !isGift(item) && item.quantity > 0 && item.original_line_price > 0);
  }

  async function removeGiftLines(data, predicate) {
    // Keys change when discounts change. Re-fetch the current key after every deletion.
    let count = 0;
    while (count++ < 12) {
      const line = (data.items || []).find(item => isGift(item) && predicate(item));
      if (!line) break;
      data = await request('cart/change.js', { id: line.key, quantity: 0 });
    }
    return data;
  }

  let applying = false;
  let reconciling = null;

  async function reconcile(input) {
    if (applying) return { cart: input || await getCart(), changed: false };
    if (reconciling) return reconciling;
    reconciling = (async () => {
      let data = input || await getCart();
      const gifts = (data.items || []).filter(isGift);
      if (!gifts.length) return { cart: data, changed: false };

      const validGift = item => {
        const campaign = match(item.properties._ys_influencer_gift);
        return !!campaign &&
          Number(item.variant_id) === Number(campaign.variantId) &&
          item.quantity === 1 &&
          item.final_line_price === 0 &&
          hasPurchasedItem(data) &&
          codeApplied(data, campaign.code);
      };

      const stale = gifts.filter(item => !validGift(item));
      if (!stale.length) return { cart: data, changed: false };

      // Never leave a gifted line as a full-price surprise for the customer.
      const staleKeys = new Set(stale.map(item => normalize(item.properties._ys_influencer_gift)));
      data = await removeGiftLines(data, item =>
        staleKeys.has(normalize(item.properties._ys_influencer_gift)));

      // Remove internal BXGY codes when their promotional item is no longer valid.
      // Only remove codes belonging to the stale gift campaign.
      // This also removes the customer code for gift-only campaigns.
      const secretCodes = new Set(stale.map(item =>
        campaigns.find(c => normalize(c.code) === normalize(item.properties._ys_influencer_gift))?.giftCode
      ).filter(Boolean).map(normalize));
      const codes = appliedCodes(data);
      const remaining = codes.filter(code => !secretCodes.has(normalize(code)));
      if (codes.some(code => secretCodes.has(normalize(code)))) {
        data = await request('cart/update.js', { discount: remaining.join(',') });
      }
      return { cart: data, changed: true };
    })().finally(() => { reconciling = null; });
    return reconciling;
  }

  async function apply(code) {
    if (applying) return { ok: false, message: 'Bitte versuche es nochmals.' };
    const campaign = match(code);
    if (!campaign) return { ok: false, message: 'Diese Aktion ist nicht aktiv.' };
    // Gift-only campaigns use the same BXGY code in both Metaobject fields.
    const giftOnly = normalize(campaign.code) === normalize(campaign.giftCode);
    applying = true;
    try {
      // Finish any previous cleanup before recreating the gift line.
      if (reconciling) await reconciling;
      let data = await getCart();
      if (!hasPurchasedItem(data)) {
        return { ok: false, message: 'Lege zuerst einen Artikel in den Warenkorb.' };
      }
      const sameGift = item => normalize(item.properties._ys_influencer_gift) === normalize(campaign.code);
      const existsFree = (data.items || []).some(item => isGift(item) && sameGift(item) &&
        item.quantity === 1 && item.final_line_price === 0);

      // A previously discounted gift may have turned back into a paid item in checkout.
      // Always remove a stale promotional line before reapplying the two codes.
      if (!existsFree || !codeApplied(data, campaign.code)) {
        data = await removeGiftLines(data, sameGift);
        await request('cart/add.js', {
          items: [{
            id: Number(campaign.variantId),
            quantity: 1,
            properties: { _ys_influencer_gift: campaign.code }
          }]
        });
      }

      data = await request('cart/update.js', {
        discount: giftOnly ? campaign.code : campaign.code + ',' + campaign.giftCode
      });

      const giftIsFree = (data.items || []).some(item =>
        isGift(item) && sameGift(item) &&
        Number(item.variant_id) === Number(campaign.variantId) &&
        item.quantity === 1 && item.final_line_price === 0);

      if (!giftIsFree || !codeApplied(data, campaign.code)) {
        data = await removeGiftLines(data, sameGift);
        // Preserve a percentage code, but not a gift-only code that no longer qualifies.
        await request('cart/update.js', { discount: giftOnly ? '' : campaign.code });
        return {
          ok: false,
          message: 'Der Rabatt konnte nicht mit dem Geschenk kombiniert werden. Bitte versuche es erneut.'
        };
      }

      return { ok: true, message: giftOnly ? 'Gratisgeschenk erfolgreich hinzugefügt!' : 'Rabatt und Gratisgeschenk erfolgreich angewendet!' };
    } catch (error) {
      console.error('Influencer gift application failed:', error);
      try {
        const data = await getCart();
        await removeGiftLines(data, item =>
          normalize(item.properties._ys_influencer_gift) === normalize(campaign.code));
        await request('cart/update.js', { discount: giftOnly ? '' : campaign.code });
      } catch (cleanupError) {
        console.error('Gift cleanup failed:', cleanupError);
      }
      return {
        ok: false,
        message: 'Geschenk konnte nicht hinzugefügt werden. Bitte versuche es erneut.'
      };
    } finally {
      applying = false;
      window.updateCartDrawer?.();
    }
  }

  window.ysInfluencerGifts = {
    matches: code => !!match(code),
    apply,
    reconcile,
    isApplying: () => applying
  };

  // When customers return from checkout, the discount may have been removed there.
  // Reconcile only if the Liquid-rendered cart already contains a promotional gift.
  const initialGift = config.dataset.hasGift === 'true';
  const onReturn = async () => {
    try {
      const result = await reconcile();
      if (result.changed) window.updateCartDrawer?.();
    } catch (error) {
      console.error('Influencer gift return check failed:', error);
    }
  };

  if (initialGift) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', onReturn, { once: true });
    } else {
      onReturn();
    }
  }

  // BFCache restores the old HTML without a new Liquid render.
  window.addEventListener('pageshow', event => {
    if (event.persisted) onReturn();
  });
})();
